import { oc } from '@orpc/contract'
import { z } from 'zod'

// ── Meeting URL validation (shared between API and frontend) ────

export const GOOGLE_MEET_REGEX = /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}$/i
export const TEAMS_REGEX =
  /^https:\/\/teams\.(microsoft\.com\/l\/meetup-join|live\.com\/meet)\/.+$/i

export function isValidMeetingUrl(url: string): boolean {
  return GOOGLE_MEET_REGEX.test(url) || TEAMS_REGEX.test(url)
}

export function getMeetingPlatform(url: string): 'google_meet' | 'teams' | null {
  if (GOOGLE_MEET_REGEX.test(url)) return 'google_meet'
  if (TEAMS_REGEX.test(url)) return 'teams'
  return null
}

/** PLAN-085: a HUB-hosted video meeting vs. an external / imported one. */
export const meetingKindSchema = z.enum(['external', 'hub'])
export const meetingLanguageSchema = z.enum(['sv', 'en'])
export type MeetingLanguage = z.infer<typeof meetingLanguageSchema>
export const meetingRoomLifecycleSchema = z.enum(['scheduled', 'live', 'ended'])
export const meetingNotesStatusSchema = z.enum(['pending', 'done', 'failed'])

export const meetingInviteeSchema = z.object({
  userId: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
})

export const meetingSchema = z.object({
  id: z.string().min(1),
  projectId: z.string().min(1),
  title: z.string(),
  description: z.string().nullable(),
  startAt: z.string(),
  endAt: z.string(),
  meetingUrl: z.string().nullable(),
  botId: z.string().nullable(),
  botStatus: z.string().nullable(),
  icsUid: z.string().nullable(),
  organizer: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  notesDocumentId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // ── PLAN-085 ──
  kind: meetingKindSchema,
  /** Unguessable join code; null for external meetings. */
  roomCode: z.string().nullable(),
  roomStatus: meetingRoomLifecycleSchema.nullable(),
  language: meetingLanguageSchema.nullable(),
  hostUserId: z.string().nullable(),
  invitees: z.array(meetingInviteeSchema),
  /** Final-notes progress after a HUB meeting ended (board m5gc #14). */
  notesStatus: meetingNotesStatusSchema.nullable(),
})

export const meetingsListContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      from: z.string().optional(),
      to: z.string().optional(),
    })
  )
  .output(
    z.object({
      items: z.array(meetingSchema),
    })
  )

export const meetingsGetContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      meetingId: z.string().min(1),
    })
  )
  .output(meetingSchema)

export const meetingsCreateContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      title: z.string().min(1),
      startAt: z.string().min(1),
      endAt: z.string().min(1),
      description: z.string().optional(),
      meetingUrl: z.string().optional(),
      /** PLAN-085 — 'hub' mints a room code, seeds notes and mails invites. */
      kind: meetingKindSchema.optional(),
      /** Project member ids; the creator is always added. */
      invitees: z.array(z.string().min(1)).optional(),
      language: meetingLanguageSchema.optional(),
    })
  )
  .output(
    meetingSchema.extend({
      /** Invitees whose invite mail could not be sent (non-fatal). */
      inviteFailures: z.array(z.string()).default([]),
    })
  )

export const meetingsUpdateContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      meetingId: z.string().min(1),
      title: z.string().min(1).optional(),
      description: z.string().nullable().optional(),
      startAt: z.string().optional(),
      endAt: z.string().optional(),
      meetingUrl: z.string().nullable().optional(),
    })
  )
  .output(meetingSchema)

export const meetingsDeleteContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      meetingId: z.string().min(1),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

export const meetingsQuickInviteContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      meetingUrl: z.string().url(),
    })
  )
  .output(
    z.object({
      meetingId: z.string(),
      status: z.enum(['joining', 'already_active']),
    })
  )

export const meetingsGenerateNotesContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      meetingId: z.string().min(1),
    })
  )
  .output(
    z.object({
      status: z.enum(['triggered', 'no_transcript']),
    })
  )

// ── PLAN-085: HUB video meetings ──────────────────────────────────────────

/** Start a meeting right now from the calendar and go straight into it. */
export const meetingsStartInstantContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      title: z.string().min(1).max(200).optional(),
      language: meetingLanguageSchema.optional(),
    })
  )
  .output(meetingSchema)

/**
 * The one place a RealtimeKit participant token is minted. The token is
 * returned once, kept in React state and never stored or logged; a re-join
 * calls this again. `rtk` is null on the dev path (MEETINGS_ALLOW_NO_RTK),
 * for an ended meeting, and while the room is still closed — see
 * `waitingUntil`.
 */
export const meetingsJoinContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      code: z.string().min(1).max(64),
    })
  )
  .output(
    z.object({
      meeting: meetingSchema,
      rtk: z
        .object({
          authToken: z.string(),
          meetingId: z.string(),
        })
        .nullable(),
      self: z.object({
        userId: z.string(),
        name: z.string(),
        isHost: z.boolean(),
        canEnd: z.boolean(),
      }),
      notesDocumentId: z.string().nullable(),
      /** Path for the room socket, e.g. `/ws/<projectId>/meeting/<meetingId>`. */
      socketPath: z.string(),
      /**
       * Set when the room is not open yet: nothing was started, no token was
       * minted, and the caller should show the waiting screen and try again
       * at this time (PLAN-085 §1.4, REVIEW-1 #1).
       */
      waitingUntil: z.string().nullable(),
    })
  )

/**
 * The participant is leaving (board fqw7). `left` is a deliberate leave —
 * the Leave button, the project-switch dialog — and writes the leave row at
 * once. `unload` is the tab going away (`pagehide`, sent as a beacon): the
 * room treats it like a closed socket, so a reload that reconnects within
 * the grace window is a resume and not a leave + join.
 */
export const meetingsLeaveContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      code: z.string().min(1).max(64),
      reason: z.enum(['left', 'unload']),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

export const meetingsEndContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      code: z.string().min(1).max(64),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

/**
 * The meeting a notes document belongs to, for the document page's
 * "HUB is writing the notes…" banner (board m5gc #14). Null for any other
 * document.
 */
export const meetingsForDocumentContract = oc
  .input(z.object({ projectId: z.string().min(1), documentId: z.string().min(1) }))
  .output(
    z.object({
      meeting: z
        .object({
          id: z.string(),
          title: z.string(),
          kind: meetingKindSchema,
          roomStatus: meetingRoomLifecycleSchema.nullable(),
          notesStatus: meetingNotesStatusSchema.nullable(),
        })
        .nullable(),
    })
  )

/** Read-only room status for the waiting screen and the meeting detail page. */
export const meetingsRoomStateContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      code: z.string().min(1).max(64),
    })
  )
  .output(
    z.object({
      status: meetingRoomLifecycleSchema,
      startedAt: z.string().nullable(),
      endedAt: z.string().nullable(),
      participants: z.array(meetingInviteeSchema.extend({ connected: z.boolean() })),
      notesDocumentId: z.string().nullable(),
      canEnd: z.boolean(),
    })
  )
